import React, {useEffect, useMemo, useRef, useState} from "react";
import {MapContainer, Marker, Polygon, TileLayer, Tooltip, useMap} from "react-leaflet";
import {css} from "@emotion/react";
import {flex} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader";
import {skipToken} from "@reduxjs/toolkit/query";
import i18nextCore from "i18next";
import L from "leaflet";

import {DistrictMapLegendItem, dynamicDistrictMapLegendValues} from "../../../utils/dynamic_district_map_legend_values";
import {formatNumber} from "../../../utils/format";
import {findMaxValue} from "../../../utils/misc";
import {rpWebUrl} from "../common/app/read_charts_web_environment_variables";
import {
    IRegionGeometry,
    useGetRegionGeometryQuery,
    useGetRegionsGeometryByParentQuery
} from "./map/api/charts_regions_api";
import {MARKER_CLUSTER_DISABLE_ZOOM} from "./map/marker_cluster_constants";
import {MarkerClusterLayer} from "./map/MarkerClusterLayer";
import {IDistrict, OpenStreetMapProps} from "./map/types/open_street_map";
import {IOsmDistrictPolygon} from "./map/types/osm_district_polygon";
import {RpRegionType} from "./map/types/rp_region_type";

// Fix for default markers in react-leaflet
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Import Leaflet CSS
import "leaflet/dist/leaflet.css";

// Configure default markers
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

const ADDED_MAP_LEGEND_COLORS = ["#FFF2D4", "#FFBD98", "#FF8F6D", "#FF6C40", "#E63600"];

const DISTRICT_STROKE_COLOR = "#333333";
const DISTRICT_STROKE_WEIGHT = 2;
const DISTRICT_THEMATIC_FILL_OPACITY = 0.55;

const formatDistrictMetricValue = (value: number, format: "default" | "percent"): string => {
    if (format === "percent") {
        return `${formatNumber(value * 100, false)}%`;
    }
    return formatNumber(value, false);
};

/** GeoJSON [lng, lat] → Leaflet [lat, lng]. MultiPolygon → `LatLng[][][]` for disjoint parts (outer rings only). */
const polygonPositions = (district: IDistrict): [number, number][] | [number, number][][][] => {
    const {type, coordinates} = district.area.geo_area;
    const ringToLatLng = (ring: [number, number][]): [number, number][] =>
        ring.map(([lng, lat]) => [lat, lng] as [number, number]);

    if (type === "MultiPolygon") {
        const parts = coordinates as [number, number][][][];
        return parts.map((poly) => [ringToLatLng(poly[0])]);
    }

    const rings = coordinates as [number, number][][];
    return ringToLatLng(rings[0]);
};

/** Region polygons often use `warszawa-mokotow` while stats use `mokotow` (Highcharts map id). */
const slugLookupCandidates = (district: IDistrict, statsCitySlug?: string): string[] => {
    const sDistrict = district.slug_district?.trim();
    const sRegion = district.slug?.trim();
    const seeds = [...new Set([sDistrict, sRegion].filter(Boolean) as string[])];
    const out = new Set<string>();
    for (const b of seeds) {
        out.add(b);
        if (statsCitySlug) {
            const prefix = `${statsCitySlug}-`;
            if (b.startsWith(prefix)) {
                out.add(b.slice(prefix.length));
            } else {
                out.add(`${prefix}${b}`);
            }
        }
    }
    return [...out];
};

const resolveDistrictAdded = (
    district: IDistrict,
    values: Record<string, number> | undefined,
    statsCitySlug?: string,
    cityOutlineMetric?: boolean
): number | null => {
    if (!values) {
        return null;
    }
    for (const key of slugLookupCandidates(district, statsCitySlug)) {
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            return values[key];
        }
    }

    const regionSlugs = [district.slug_district?.trim(), district.slug?.trim()].filter(Boolean) as string[];
    for (const regionSlug of regionSlugs) {
        for (const [statsKey, metric] of Object.entries(values)) {
            if (regionSlug === statsKey || regionSlug.endsWith(`-${statsKey}`)) {
                return metric;
            }
        }
    }

    if (cityOutlineMetric) {
        const metrics = Object.values(values);

        if (metrics.length === 1) {
            return metrics[0];
        }
    }

    return null;
};

const normalizeRegionListMapResponse = (data: unknown): IDistrict[] => {
    if (Array.isArray(data)) {
        return data as IDistrict[];
    }
    if (data != null && typeof data === "object") {
        const withResults = data as {results?: IDistrict[]};
        if (Array.isArray(withResults.results)) {
            return withResults.results;
        }
        if ("area" in data) {
            return [data as IDistrict];
        }
    }
    return [];
};

const regionGeometryToDistrict = (regionGeometry: IRegionGeometry): IDistrict | null => {
    if (regionGeometry.geo_area == null) {
        return null;
    }
    return {
        id: regionGeometry.id,
        type: regionGeometry.type,
        slug: regionGeometry.slug,
        slug_district: regionGeometry.slug,
        short_name: regionGeometry.name ?? regionGeometry.slug,
        full_name_reverted: regionGeometry.full_name ?? regionGeometry.name ?? regionGeometry.slug,
        area: {geo_area: regionGeometry.geo_area},
        name_declension_what: "",
        name_declension_where: ""
    };
};

const hasDistrictGeometry = (district: IDistrict): boolean => {
    const geo = district.area?.geo_area;
    if (!geo?.coordinates?.length) {
        return false;
    }
    if (geo.type === "MultiPolygon") {
        const parts = geo.coordinates as [number, number][][][];
        return parts.some((part) => (part?.[0]?.length ?? 0) > 0);
    }
    const rings = geo.coordinates as [number, number][][];
    return (rings?.[0]?.length ?? 0) > 0;
};

/** Grow bounds to include one district geometry (Polygon vs MultiPolygon layouts differ). */
const extendDistrictBounds = (bounds: L.LatLngBounds, district: IDistrict): void => {
    const pos = polygonPositions(district);
    if (district.area.geo_area.type === "MultiPolygon") {
        for (const part of pos as [number, number][][][]) {
            for (const ring of part) {
                for (const ll of ring) {
                    bounds.extend(ll);
                }
            }
        }
        return;
    }
    for (const ll of pos as [number, number][]) {
        bounds.extend(ll);
    }
};

/** Keeps map in sync when city (center/zoom) or district polygons change — MapContainer ignores prop updates otherwise. */
const MapViewController = ({
    centerLat,
    centerLng,
    zoom,
    districts,
    markers,
    fitMaxZoom,
    fitBoundsZoomOffset = 0
}: {
    centerLat: number;
    centerLng: number;
    zoom: number;
    districts: IDistrict[];
    markers: Array<{coords: [number, number]}>;
    fitMaxZoom: number;
    fitBoundsZoomOffset?: number;
}) => {
    const map = useMap();

    // Marker objects get a new array reference whenever only their icon/popup changes (e.g. active marker
    // on click) — key on coords so the fit-bounds effect below doesn't re-run (and re-zoom) for that.
    const markerCoordsKey = useMemo(() => markers.map((m) => m.coords.join(",")).join("|"), [markers]);

    useEffect(() => {
        map.setView([centerLat, centerLng], zoom);
    }, [map, centerLat, centerLng, zoom]);

    useEffect(() => {
        const applyFitBounds = (b: L.LatLngBounds) => {
            if (!b.isValid()) {
                return;
            }
            map.fitBounds(b, {padding: [28, 28], maxZoom: fitMaxZoom});
            if (fitBoundsZoomOffset !== 0) {
                const nextZoom = map.getZoom() + fitBoundsZoomOffset;
                map.setZoom(Math.max(Math.min(nextZoom, fitMaxZoom), map.getMinZoom()));
            }
        };

        if (districts.length > 0) {
            const b = L.latLngBounds([]);
            for (const d of districts) {
                extendDistrictBounds(b, d);
            }
            applyFitBounds(b);
            return;
        }

        if (markers.length === 0) {
            return;
        }

        const markerBounds = L.latLngBounds(markers.map((m) => m.coords));
        applyFitBounds(markerBounds);
    }, [map, districts, markerCoordsKey, fitMaxZoom, fitBoundsZoomOffset]);

    return null;
};

/** Leaflet does not always notice flex/grid width changes; keep tiles aligned when the map column resizes. */
const MapResizeInvalidator = () => {
    const map = useMap();

    useEffect(() => {
        const el = map.getContainer();
        const ro = new ResizeObserver(() => {
            map.invalidateSize({animate: false});
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [map]);

    return null;
};

/**
 * `MapOfferBox` over the map from the left: instant `panBy(inset, 0)` once when selection opens (`null` -> id).
 * With overlay already open, switching markers does not move the map (listing-map parity with former Google overlay).
 */
const PanToOfferController = ({
    panToOfferId,
    focusOfferLeftInsetPx = 0
}: {
    panToOfferId?: string | number | null;
    focusOfferLeftInsetPx?: number;
}) => {
    const map = useMap();
    const prevOfferRef = useRef<string | number | null | undefined>(undefined);

    useEffect(() => {
        const inset = Math.max(0, focusOfferLeftInsetPx ?? 0);

        if (panToOfferId == null) {
            prevOfferRef.current = null;
            return;
        }

        const previous = prevOfferRef.current;
        prevOfferRef.current = panToOfferId;

        if (previous !== null && previous !== undefined) {
            return;
        }
        if (inset <= 0) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            map.invalidateSize(false);
            map.panBy(L.point(inset, 0), {animate: false});
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [map, panToOfferId, focusOfferLeftInsetPx]);

    return null;
};

/** No fill when stats missing (`resolveDistrictAdded === null`), no legend, or value outside buckets — outline only. */
const districtPolygonPathOptions = (
    district: IDistrict,
    districtValuesBySlug: Record<string, number> | undefined,
    legend: DistrictMapLegendItem[],
    statsCitySlug?: string,
    cityOutlineMetric?: boolean
): L.PathOptions => {
    const value = resolveDistrictAdded(district, districtValuesBySlug, statsCitySlug, cityOutlineMetric);

    const outlineOnly: L.PathOptions = {
        color: DISTRICT_STROKE_COLOR,
        weight: DISTRICT_STROKE_WEIGHT,
        fillOpacity: 0
    };

    if (value === null || legend.length === 0) {
        return outlineOnly;
    }

    const fillColor = legend.find((el) => value >= el[0] && (el[1] === Infinity ? value <= el[1] : value < el[1]))?.[3];
    if (fillColor == null) {
        return outlineOnly;
    }

    return {
        color: DISTRICT_STROKE_COLOR,
        weight: DISTRICT_STROKE_WEIGHT,
        fillColor,
        fillOpacity: DISTRICT_THEMATIC_FILL_OPACITY
    };
};

const OpenStreetMap = ({
    center = [50.0647, 19.945],
    zoom = 10,
    height = "100%",
    className,
    markers = [],
    markerClustering = false,
    disableClusteringAtZoom = MARKER_CLUSTER_DISABLE_ZOOM,
    scrollWheelZoom = true,
    maxZoom = 18,
    regionsMapSlug,
    regionsMapParentId,
    regionsMapChildType,
    regionsMapRegionId,
    districtValuesBySlug,
    districtStatsCitySlug,
    addedMetricLabel,
    legendColors = ADDED_MAP_LEGEND_COLORS,
    legendItems,
    legendType = "quantitative",
    districtMetricFormat = "default",
    legendSize = "default",
    fitBoundsZoomOffset = 0,
    panToOfferId,
    focusOfferLeftInsetPx = 0,
    districtPolygonTooltip,
    markersLoading = false,
    districtDrillDownEnabled = false,
    onDistrictPolygonClick,
    cityOutlineMetric = false,
    regionGeometry
}: OpenStreetMapProps) => {
    const addedLabel = addedMetricLabel ?? i18nextCore.t("fields.added");
    const showDistrictPolygonTooltip =
        districtPolygonTooltip === true ||
        (districtPolygonTooltip !== false &&
            districtValuesBySlug != null &&
            Object.keys(districtValuesBySlug).length > 0);
    const [mapMounted, setMapMounted] = useState(false);
    const [districtsData, setDistrictsData] = useState<IDistrict[]>([]);

    useEffect(() => {
        setMapMounted(true);
    }, []);

    const legend = useMemo(() => {
        const values = Object.values(districtValuesBySlug ?? {});
        const highestValue = findMaxValue(values);
        if (!districtValuesBySlug || highestValue <= 0) {
            return [];
        }
        if (legendItems && legendItems.length > 0) {
            return legendItems;
        }
        return dynamicDistrictMapLegendValues(highestValue, legendType, legendColors);
    }, [districtValuesBySlug, legendColors, legendItems, legendType]);

    // Single region by id and children-by-parent already have typed RTK Query endpoints — no need for the
    // legacy `region-list-map` fetch below for those cases. Slug-only lookup still lacks an endpoint there.
    const regionIdGeometryQueryArg =
        regionGeometry === undefined && regionsMapParentId == null && regionsMapRegionId != null
            ? regionsMapRegionId
            : skipToken;
    const {data: regionIdGeometry} = useGetRegionGeometryQuery(regionIdGeometryQueryArg);

    const regionsGeometryByParentQueryArg =
        regionGeometry === undefined && regionsMapParentId != null
            ? {parentId: regionsMapParentId, type: regionsMapChildType ?? RpRegionType.DISTRICT}
            : skipToken;
    const {data: regionsGeometryByParent} = useGetRegionsGeometryByParentQuery(regionsGeometryByParentQueryArg);

    useEffect(() => {
        if (regionGeometry !== undefined) {
            const district = regionGeometry != null ? regionGeometryToDistrict(regionGeometry) : null;
            setDistrictsData(district != null ? [district] : []);
            return;
        }

        if (regionsMapParentId != null) {
            if (regionsGeometryByParent === undefined) {
                return;
            }
            setDistrictsData(
                regionsGeometryByParent.map(regionGeometryToDistrict).filter((d): d is IDistrict => d != null)
            );
            return;
        }

        if (regionsMapRegionId != null) {
            if (regionIdGeometry === undefined) {
                return;
            }
            const district = regionGeometryToDistrict(regionIdGeometry);
            setDistrictsData(district != null ? [district] : []);
            return;
        }

        if (!regionsMapSlug) {
            setDistrictsData([]);
            return;
        }

        const url = `${rpWebUrl}/api/v2/regions/region/?s=region-list-map&page_size=1000&slug=${encodeURIComponent(regionsMapSlug)}`;
        const controller = new AbortController();
        let cancelled = false;

        const fetchDistricts = async () => {
            try {
                const response = await fetch(url, {
                    method: "GET",
                    signal: controller.signal,
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    }
                });
                if (!response.ok) {
                    if (!cancelled) {
                        setDistrictsData([]);
                    }
                    return;
                }
                const data: unknown = await response.json();
                const districts = normalizeRegionListMapResponse(data).filter(hasDistrictGeometry);
                if (!cancelled) {
                    setDistrictsData(districts);
                }
            } catch (error: unknown) {
                if ((error as {name?: string}).name === "AbortError") {
                    return;
                }
                if (!cancelled) {
                    setDistrictsData([]);
                }
            }
        };

        void fetchDistricts();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        regionGeometry,
        regionIdGeometry,
        regionsGeometryByParent,
        regionsMapParentId,
        regionsMapRegionId,
        regionsMapSlug
    ]);

    const centerLat = center[0];
    const centerLng = center[1];
    const useMarkerClustering = markerClustering && markers.length > 0;

    return (
        <div css={wrapper} style={{height, width: "100%"}} className={className}>
            <div css={mapPane}>
                {markersLoading ? (
                    <div css={markersLoadingOverlay} aria-busy="true" aria-live="polite">
                        <Loader size="lg" />
                    </div>
                ) : null}
                {mapMounted ? (
                    <MapContainer
                        center={center}
                        zoom={zoom}
                        scrollWheelZoom={scrollWheelZoom}
                        boxZoom
                        maxZoom={maxZoom}
                        style={{height: "100%", width: "100%"}}
                    >
                        <MapViewController
                            centerLat={centerLat}
                            centerLng={centerLng}
                            zoom={zoom}
                            districts={districtsData}
                            markers={markers}
                            fitMaxZoom={maxZoom}
                            fitBoundsZoomOffset={fitBoundsZoomOffset}
                        />
                        <PanToOfferController
                            panToOfferId={panToOfferId}
                            focusOfferLeftInsetPx={focusOfferLeftInsetPx}
                        />
                        <MapResizeInvalidator />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {districtsData.filter(hasDistrictGeometry).map((district) => {
                            const added = resolveDistrictAdded(
                                district,
                                districtValuesBySlug,
                                districtStatsCitySlug,
                                cityOutlineMetric
                            );
                            const formattedValue = added === null ? "—" : formatNumber(added, false);

                            const districtClickPayload: IOsmDistrictPolygon = {
                                id: district.id,
                                slug: district.slug,
                                slug_district: district.slug_district,
                                type: district.type,
                                full_name_reverted: district.full_name_reverted,
                                short_name: district.short_name
                            };

                            return (
                                <Polygon
                                    key={district.id}
                                    positions={polygonPositions(district)}
                                    pathOptions={districtPolygonPathOptions(
                                        district,
                                        districtValuesBySlug,
                                        legend,
                                        districtStatsCitySlug,
                                        cityOutlineMetric
                                    )}
                                    interactive
                                    className={districtDrillDownEnabled ? "district-drilldown" : undefined}
                                    eventHandlers={
                                        districtDrillDownEnabled && onDistrictPolygonClick
                                            ? {click: () => onDistrictPolygonClick(districtClickPayload)}
                                            : undefined
                                    }
                                >
                                    {showDistrictPolygonTooltip ? (
                                        <Tooltip
                                            sticky
                                            direction="top"
                                            opacity={0.95}
                                            className={districtTooltipClass(legendSize)}
                                        >
                                            <div css={tooltipBody(legendSize)}>
                                                <strong>{district.full_name_reverted}</strong>
                                                <div css={tooltipMetric(legendSize)}>
                                                    <span css={tooltipMetricLabel(legendSize)}>{addedLabel}:</span>
                                                    <span css={tooltipMetricValue(legendSize)}>{formattedValue}</span>
                                                </div>
                                            </div>
                                        </Tooltip>
                                    ) : null}
                                </Polygon>
                            );
                        })}

                        {useMarkerClustering ? (
                            <MarkerClusterLayer markers={markers} disableClusteringAtZoom={disableClusteringAtZoom} />
                        ) : (
                            markers.map((marker) => (
                                <Marker
                                    key={marker.id}
                                    position={marker.coords}
                                    icon={
                                        marker.icon
                                            ? L.icon({
                                                  iconUrl: marker.icon.url,
                                                  iconSize: marker.icon.sizes,
                                                  iconAnchor: [marker.icon.sizes[0] / 2, marker.icon.sizes[1]],
                                                  tooltipAnchor: [0, -(marker.icon.sizes[1] + 8)],
                                                  popupAnchor: [0, -Math.max(marker.icon.sizes[1] - 4, 8)]
                                              })
                                            : undefined
                                    }
                                    eventHandlers={marker.onClick ? {click: marker.onClick} : undefined}
                                >
                                    {marker.popup ? (
                                        <Tooltip direction="top" opacity={0.95}>
                                            <div dangerouslySetInnerHTML={{__html: marker.popup}} />
                                        </Tooltip>
                                    ) : null}
                                </Marker>
                            ))
                        )}
                    </MapContainer>
                ) : null}
            </div>

            {legend.length > 0 && (
                <div css={legendRow(legendSize)} aria-hidden>
                    {legend.map(([minV, maxV, label, color], i) => (
                        <div key={`${minV}-${maxV}-${i}`} css={legendItem(legendSize)}>
                            <span css={legendDot(color, legendSize)} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const wrapper = css`
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    min-height: 0;

    & .leaflet-container {
        width: 100%;
        height: 100%;
    }

    & .charts-marker-cluster {
        background: transparent !important;
        border: none;
    }

    & .leaflet-popup-content {
        margin: 0;
    }

    & .leaflet-tooltip {
        border-radius: 8px;
    }

    & .leaflet-tooltip.charts-district-tooltip--default {
        padding: 8px 13px;
        font-size: 0.9375rem;
        line-height: 1.35;
    }

    & .leaflet-tooltip.charts-district-tooltip--large {
        padding: 10px 15px;
        font-size: 1.125rem;
        line-height: 1.35;
    }

    & .leaflet-tooltip.charts-district-tooltip--xlarge {
        padding: 13px 18px;
        font-size: 1.33rem;
        line-height: 1.35;
    }

    & .leaflet-interactive.district-drilldown {
        cursor: pointer;
    }
`;

const mapPane = css`
    flex: 1;
    min-height: 0;
    position: relative;
`;

const markersLoadingOverlay = css`
    position: absolute;
    inset: 0;
    z-index: 1000;
    ${flex("center", "center")};
    background-color: rgba(255, 255, 255, 0.65);
    pointer-events: none;
`;

type DistrictLegendSize = NonNullable<OpenStreetMapProps["legendSize"]>;

const legendLayout = (size: DistrictLegendSize) => {
    switch (size) {
        case "xlarge":
            return {
                gap: "1.44rem 2rem",
                padding: "1.375rem 1.06rem 0.31rem",
                fontSize: "1.33rem",
                itemGap: "0.69rem",
                dot: "19px"
            };
        case "large":
            return {
                gap: "1.19rem 1.56rem",
                padding: "1.125rem 0.94rem 0.19rem",
                fontSize: "1.125rem",
                itemGap: "0.56rem",
                dot: "15px"
            };
        default:
            return {
                gap: "0.94rem 1.25rem",
                padding: "0.94rem 0.63rem 0",
                fontSize: "0.9375rem",
                itemGap: "0.44rem",
                dot: "13px"
            };
    }
};

const districtTooltipClass = (size: DistrictLegendSize) => `charts-district-tooltip--${size}`;

const tooltipBody = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        text-align: left;
        line-height: 1.35;
        font-size: ${L.fontSize};
    `;
};

const tooltipMetric = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.35em;
        margin-top: ${L.itemGap};
        font-size: ${L.fontSize};
    `;
};

const tooltipMetricLabel = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        font-weight: 500;
        color: #5c5c5c;
        font-size: ${L.fontSize};
    `;
};

const tooltipMetricValue = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        font-weight: 700;
        color: #1a1a1a;
        font-size: calc(${L.fontSize} * 1.12);
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.02em;
    `;
};

const legendRow = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        display: flex;
        flex-shrink: 0;
        flex-wrap: wrap;
        justify-content: center;
        gap: ${L.gap};
        padding: ${L.padding};
        font-size: ${L.fontSize};
        font-weight: 600;
        color: #333333;
    `;
};

const legendItem = (legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        display: flex;
        align-items: center;
        gap: ${L.itemGap};
    `;
};

const legendDot = (color: string, legendSize: DistrictLegendSize) => {
    const L = legendLayout(legendSize);
    return css`
        width: ${L.dot};
        height: ${L.dot};
        border-radius: 2px;
        background: ${color};
        flex-shrink: 0;
    `;
};

export default OpenStreetMap;
